import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import LoadingButton from "./LoadingButton";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";

import { pdfjs } from "react-pdf";
import "pdfjs-dist/web/pdf_viewer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const SignUp = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [privacyPolicyViewed, setPrivacyPolicyViewed] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [termsAndConditionsViewed, setTermsAndConditionsViewed] =
    useState(false);
  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastList, setToastList] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    password: "",
    confirmPassword: "",
    // userType: "",
    // applicatorId: "",
  });
  // const [otp, setOtp] = useState(""); // For tracking OTP input
  // const [loading, setLoading] = useState(false);
  // const [isSignUpDisabled, setIsSignUpDisabled] = useState(false); // To disable Sign Up button
  // const [isRequestDisabled, setIsRequestDisabled] = useState(true); // To disable Request button
  // const [otpFilled, setOtpFilled] = useState(false); // To track OTP input
  // const [isSignupComplete, setIsSignupComplete] = useState(false); // Tracks if signup is complete
  const [termsColor, setTermsColor] = useState("#00B5ED");
  const [privacyColor, setPrivacyColor] = useState("#00B5ED");

  // // Update OTP state
  // const handleOtpChange = (e) => {
  //   setOtp(e.target.value);
  //   setOtpFilled(e.target.value.trim() !== ""); // Enable Sign Up button when OTP is filled
  // };

  const passwordsMatch =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword;

  const showPasswordMismatch =
    formData.confirmPassword.length > 0 &&
    formData.password !== formData.confirmPassword;

  const passwordRequirements = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /\d/.test(formData.password),
    specialChar: /[@$!%*?&#]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

  useEffect(() => {
    // Enabling checkbox when both documents are viewed
    if (privacyPolicyViewed && termsAndConditionsViewed) {
      setIsCheckboxEnabled(true);
      setIsCheckboxChecked(true);
    }
  }, [privacyPolicyViewed, termsAndConditionsViewed]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // setIsSignUpDisabled(true); // Disable Sign Up after it's clicked
    // setIsRequestDisabled(false); // Enable Request button after successful signup

    // Save the email to localStorage upon form submission
    localStorage.setItem("signupEmail", formData.email);
    // console.log("Form submitted:", formData.email);

    setIsLoading(true);
    // Simulate a delay (e.g., for navigation or API call)
    setTimeout(() => {
      setIsLoading(false);
    }, 30000);

    // Check if password is strong before submission
    if (!isPasswordValid) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description:
            "Weak password! Must have 8+ characters, uppercase, lowercase, number, and special character.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Passwords do not match!",
          variant: "destructive",
        },
      ]);

      setIsLoading(false);
      // setIsSignUpDisabled(false);
      // setIsRequestDisabled(true); // Disable Request button
      return false;
    }

    // Construct formData to exclude applicatorId when userType is not "applicator"
    const dataToSubmit = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      companyName: formData.companyName,
      password: formData.password,
      // userType: formData.userType,
      // ...(formData.userType === "applicator" && {
      //   applicatorId: formData.applicatorId,
      // }), // Include only if applicator
    };

    try {
      const response = await fetch(`${API_URL}/api/user/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
        },
        body: JSON.stringify(dataToSubmit),
      });

      if (response.ok) {
        const responseData = await response.json();
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description: responseData.data?.message || "Sign up successful!",
            variant: "default",
          },
        ]);
        handleRequestOTP();
        navigate("/verify");

        return true;
      } else {
        const responseData = await response.json();

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message || "Signup failed. Please try again.",
            variant: "destructive",
          },
        ]);
        setIsLoading(false);
        // setIsSignUpDisabled(false); // Re-enable Sign Up if there's an error
        // setIsRequestDisabled(true); // Disable Request button

        return false;
      }
    } catch (error) {
      console.error("Error:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
      // setIsSignUpDisabled(false); // Re-enable Sign Up if there's an error
      // setIsRequestDisabled(true); // Disable Request button
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  const handlePrivacyPolicyClick = (url) => {
    window.open(url, "_blank");
    setPrivacyPolicyViewed(true);
    setPrivacyColor("#22c55e");
  };

  const handleTermsAndConditionsClick = (url) => {
    window.open(url, "_blank");
    setTermsAndConditionsViewed(true);
    setTermsColor("#22c55e");
  };

  // Function to request OTP
  const handleRequestOTP = async () => {
    if (!formData.email) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "Email is not available. Please sign up again.",
          variant: "destructive",
        },
      ]);

      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/user/request-otp/${formData.email}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          },
        },
      );

      if (response.ok) {
        const responseData = await response.json();
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description:
              responseData.data?.message || "OTP sent to your email!",
            variant: "default",
          },
        ]);
      } else {
        const responseData = await response.json();

        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message ||
              "Failed to send OTP. Please try again.",
            variant: "destructive",
          },
        ]);
      }
    } catch (error) {
      console.error("Error requesting OTP:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
    }
  };

  // Function to request OTP
  // const handleRequestOTP = async (e) => {
  //   e.preventDefault();

  //   // setIsRequestDisabled(true); // Disable Request button after it's clicked
  //   setIsSignUpDisabled(false); // Enable Sign Up button after Request is clicked

  //   const email = formData.email; // Capture email from formData
  //   if (!email) {
  //     setAlert({
  //       message: "Email is not available. Please sign up again.",
  //       type: "destructive",
  //       visible: true,
  //     });
  //     // setIsRequestDisabled(false); // Re-enable Request button if there's an error
  //     return;
  //   }

  //   try {
  //     const response = await fetch(
  //       `https://licensee-service.onrender.com/api/user/request-otp/${email}`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
  //         },
  //       }
  //     );

  //     setLoading(true);

  //     if (response.ok) {
  //       setAlert({
  //         message: "OTP sent to your email!",
  //         type: "default",
  //         visible: true,
  //       });
  //     } else {
  //       setAlert({
  //         message: "Failed to send OTP. Please try again.",
  //         type: "destructive",
  //         visible: true,
  //       });
  //       // setIsRequestDisabled(false); // Re-enable Request button if there's an error
  //     }
  //   } catch (error) {
  //     console.error("Error requesting OTP:", error);
  //     setAlert({
  //       message: "Error requesting OTP. Please try again.",
  //       type: "destructive",
  //       visible: true,
  //     });
  //     // setIsRequestDisabled(false); // Re-enable Request button if there's an error
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Function to verify OTP
  // const handleVerifyOTP = async (e) => {
  //   e.preventDefault();

  //   if (!otp) {
  //     setAlert({
  //       message: "Please enter the OTP.",
  //       type: "destructive",
  //       visible: true,
  //     });
  //     // setIsRequestDisabled(false);
  //     return false;
  //   }

  //   try {
  //     const response = await fetch(
  //       `https://licensee-service.onrender.com/api/user/verify/${formData.email}`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
  //         },
  //         body: JSON.stringify({ otp }),
  //       }
  //     );

  //     if (response.ok) {
  //       setAlert({
  //         message: "OTP verified successfully!",
  //         type: "default",
  //         visible: true,
  //       });
  //       return true;
  //     } else {
  //       setAlert({
  //         message: "Invalid OTP. Please try again.",
  //         type: "destructive",
  //         visible: true,
  //       });
  //       return false;
  //     }
  //   } catch (error) {
  //     console.error("Error verifying OTP:", error);
  //     setAlert({
  //       message: "Error verifying OTP. Please try again.",
  //       type: "destructive",
  //       visible: true,
  //     });
  //     return false;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // // Function to handle both signup and OTP verification
  // const handleSignUpAndVerify = async (e) => {
  //   e.preventDefault();

  //   try {
  //     if (!isSignupComplete) {
  //       // Submit the signup form on the first click
  //       const signUpSuccessful = await handleSubmit(e);
  //       if (signUpSuccessful) {
  //         setIsSignupComplete(true); // Mark signup as complete
  //         setAlert({
  //           message: "Signup successful! Enter OTP to verify.",
  //           type: "default",
  //           visible: true,
  //         });
  //       }
  //     } else {
  //       // Verify OTP on the second click
  //       const otpVerified = await handleVerifyOTP(e);
  //       if (otpVerified) {
  //         navigate("/signin"); // Navigate on successful OTP verification
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error during signup and verification:", error);
  //     setAlert({
  //       message: "An unexpected error occurred. Please try again.",
  //       type: "destructive",
  //       visible: true,
  //     });
  //   }
  // };

  return (
    <div className="tw-flex tw-flex-col tw-min-h-screen">
      {/* <div className="tw-absolute -tw-inset-24 tw-z-0 md:tw-w-[54%] tw-hidden tw-min-h-full md:tw-flex md:tw-flex-1 tw-bg-[#f1fcff]"></div>
      <div className="tw-hidden md:tw-flex md:tw-w-[50%] tw-relative tw-min-h-screen">
        <div className="tw-flex md:tw-w-[70%] tw-justify-center tw-items-center tw-h-full tw-absolute tw-inset-0 tw-z-0">
          <img
            src="/authimg.png"
            alt="Sign Up Illustration"
            className="md:tw-h-[40%]"
          />
        </div>
      </div> */}

      <div className="tw-flex tw-flex-col tw-h-screen  tw-items-center tw-w-full tw-justify-center">
        <div className="md:tw-w-[70%]">
          <Card className="tw-w-full tw-shadow-none tw-border-none tw-mb-0 tw-bg-transparent">
            <CardHeader className="tw-px-0">
              <CardTitle className="tw-text-xl md:tw-text-3xl h-sm:tw-text-2xl">
                Sign Up
              </CardTitle>
              <CardDescription className="tw-text-[#828282]">
                Create an account
              </CardDescription>
            </CardHeader>
            <CardContent className="tw-px-0">
              <form onSubmit={handleSubmit}>
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-1 tw-gap-6 md:tw-gap-8">
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="firstName">
                      First Name
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      type="text"
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="lastName">
                      Last Name
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      type="text"
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="email">
                      Email
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      type="email"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="companyName">
                      Company Name
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      type="text"
                      placeholder="Enter company name"
                      required
                    />
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="password">
                      Password
                    </Label>
                    <div className="tw-relative">
                      <Input
                        className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm tw-pr-10"
                        id="password"
                        value={formData.password}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        required
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={() => setIsPasswordFocused(false)}
                        pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*?&]).{8,}"
                        title="Password must contain at least 8 characters, including uppercase, lowercase, numbers, and special characters."
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="tw-absolute tw-right-3 tw-top-2 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
                        aria-label="Toggle password visibility"
                        tabIndex="-1"
                      >
                        {showPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                    {(isPasswordFocused || !isPasswordValid) && (
                      <div className="tw-text-xs tw-mt-2 tw-space-y-1">
                        <p
                          className={
                            passwordRequirements.length
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.length ? "✅" : "⬜"} At least 8
                          characters
                        </p>

                        <p
                          className={
                            passwordRequirements.uppercase
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.uppercase ? "✅" : "⬜"} One
                          uppercase letter (A–Z)
                        </p>

                        <p
                          className={
                            passwordRequirements.lowercase
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.lowercase ? "✅" : "⬜"} One
                          lowercase letter (a–z)
                        </p>

                        <p
                          className={
                            passwordRequirements.number
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.number ? "✅" : "⬜"} One number
                          (0–9)
                        </p>

                        <p
                          className={
                            passwordRequirements.specialChar
                              ? "tw-text-green-600"
                              : "tw-text-gray-500"
                          }
                        >
                          {passwordRequirements.specialChar ? "✅" : "⬜"} One
                          special character (!@#$%)
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="confirmPassword">
                      Confirm Password
                    </Label>
                    <div className="tw-relative">
                      <Input
                        className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm tw-pr-10"
                        id="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Enter password"
                        required
                        disabled={!isPasswordValid}
                      />
                      <button
                        type="button"
                        onClick={toggleConfirmPasswordVisibility}
                        className="tw-absolute tw-right-3 tw-top-2 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
                        aria-label="Toggle password visibility"
                        tabIndex="-1"
                      >
                        {showConfirmPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                    {passwordsMatch && (
                      <p className="tw-text-xs tw-text-green-600 tw-font-medium">
                        ✅ Password match
                      </p>
                    )}

                    {showPasswordMismatch && (
                      <p className="tw-text-xs tw-text-red-500 tw-font-medium">
                        ❌ Password does not match
                      </p>
                    )}
                  </div>

                  {/* {formData.userType === "applicator" && (
                    <div className="tw-grid tw-gap-2 ">
                      <Label htmlFor="applicatorId">Licensee Code</Label>
                      <Input
                        id="applicatorId"
                        value={formData.applicatorId}
                        onChange={handleChange}
                        type="text"
                        placeholder="Enter licensee code"
                        required
                      />
                    </div>
                  )} */}
                </div>

                <p className="tw-text-center tw-text-[10px] tw-mt-3 tw-italic tw-text-gray-500">
                  Click on both terms to get the checkbox ticked automatically
                </p>

                <div className="tw-flex tw-items-center tw-space-x-2 tw-justify-center tw-mt-1">
                  <Checkbox
                    id="termsCheckbox"
                    disabled={!isCheckboxEnabled}
                    checked={isCheckboxChecked}
                    onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                    required
                  />
                  <span className="tw-text-xs md:tw-text-sm">
                    I agree to the terms of the{" "}
                    <span
                      type="text"
                      onClick={() =>
                        handlePrivacyPolicyClick("/privacypolicy.pdf")
                      }
                      className="tw-cursor-pointer tw-font-medium hover:tw-underline"
                      style={{ color: privacyColor }}
                    >
                      Privacy Policy
                    </span>{" "}
                    and the{" "}
                    <span
                      type="text"
                      onClick={() =>
                        handleTermsAndConditionsClick("/terms.pdf")
                      }
                      className="tw-cursor-pointer tw-font-medium hover:tw-underline"
                      style={{ color: termsColor }}
                    >
                      Site Use Terms and Conditions
                    </span>
                  </span>
                </div>

                {isLoading ? (
                  <LoadingButton />
                ) : (
                  <Button
                    type="submit"
                    disabled={!isCheckboxEnabled}
                    className="tw-w-full tw-font-semibold tw-mt-6 md:tw-mt-8 tw-text-sm"
                  >
                    SIGN UP
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
          <div className="tw-text-center tw-text-xs md:tw-text-sm tw-mb-6">
            Already have an account?{" "}
            <Link to="/signin">
              <span className="tw-text-[#00B5ED] tw-font-medium">Sign in</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default SignUp;

//  <div className="tw-relative tw-grid tw-gap-2">
//                     <Label className="tw-font-medium" htmlFor="userType">
//                       User Type
//                     </Label>
//                     <select
//                       className="input-group select-style col-sm-2 col-form-label col-form-label-sm tw-appearance-none tw-pr-8"
//                       id="userType"
//                       name="userType"
//                       value={formData.userType}
//                       onChange={handleChange}
//                       required
//                     >
//                       <option value="" hidden>
//                         Select User Type
//                       </option>
//                       <option value="applicator">Applicator</option>
//                       <option value="customer">Customer</option>
//                     </select>
//                     {/* Custom ChevronDown icon */}
//                     <ChevronDown className="tw-absolute tw-top-[70%] tw-right-4 md:tw-top-2/3 md:tw-right-1 tw-transform -tw-translate-y-1/2 tw-pointer-events-none tw-text-gray-500 tw-h-4 tw-w-4 tw-opacity-50 md:tw-mr-2" />
//                   </div>
